import React, { useState, useEffect } from 'react';
import './ProductSingle.css';

import { PageTitle, Product, SidebarHeading } from '../../../components';
import { productsContent } from '../../../constants';
import { calculateAverageRating, countCommentsAndReplies, useWishlist, useCart } from '../../../utils';

import { FaSquare, FaStar } from 'react-icons/fa';
import { BsHeart } from 'react-icons/bs';

import { Link, useLocation } from 'react-router-dom';
import ImageGallery from 'react-image-gallery';



const ProductSingle = () => {

  const location = useLocation(); // Getting the location from React Router (used for accessing passed state)
  const product = location.state; // Product data passed from the previous page


  /*--------------- 8-Related Products ---------------*/
  // Extract category and tags for filtering related products
  const mainProductCategoryTitles = product.category.map((cat) => cat.title);
  const mainProductTags = product.tags;

  // Filter and limit related products based on matching categories or tags
  const filteredRelatedProducts = productsContent
    .filter((relatedProduct) => {
      // Check if categories or tags match
      const hasMatchingCategory = relatedProduct.category.some((cat) => mainProductCategoryTitles.includes(cat.title));
      const hasMatchingTag = relatedProduct.tags.some((tag) => mainProductTags.includes(tag));

      return relatedProduct.id !== product.id && (hasMatchingCategory || hasMatchingTag);
    })
    .sort((a, b) => {
      // Further sorting logic based on category and tag matching
      const aCategoryTagMatch = a.category.some((cat) => mainProductCategoryTitles.includes(cat.title)) &&
                                a.tags.some((tag) => mainProductTags.includes(tag));
      const bCategoryTagMatch = b.category.some((cat) => mainProductCategoryTitles.includes(cat.title)) &&
                                b.tags.some((tag) => mainProductTags.includes(tag));

      if (aCategoryTagMatch && !bCategoryTagMatch) return -1;
      if (!aCategoryTagMatch && bCategoryTagMatch) return 1;

      // If both have category or tag matches, prioritize by category match
      const aCategoryOnlyMatch = a.category.some((cat) => mainProductCategoryTitles.includes(cat.title));
      const bCategoryOnlyMatch = b.category.some((cat) => mainProductCategoryTitles.includes(cat.title));

      if (aCategoryOnlyMatch && !bCategoryOnlyMatch) return -1;
      if (!aCategoryOnlyMatch && bCategoryOnlyMatch) return 1;

      // Lastly, prioritize by tag match
      const aTagOnlyMatch = a.tags.some((tag) => mainProductTags.includes(tag));
      const bTagOnlyMatch = b.tags.some((tag) => mainProductTags.includes(tag));

      if (aTagOnlyMatch && !bTagOnlyMatch) return -1;
      if (!aTagOnlyMatch && bTagOnlyMatch) return 1;

      return 0; // If no priority condition is met, maintain original order
    })
    .slice(0, 3); // Limiting the number of related products to display (4)

  /*--------------- 9-Product Gallery ---------------*/
  // Create an array of images for the image gallery
  const image = [
    {
      original: product.gallery.image1,
      thumbnail: product.gallery.image1,
    },
    {
      original: product.gallery.image2,
      thumbnail: product.gallery.image2,
    },
    {
      original: product.gallery.image3,
      thumbnail: product.gallery.image3,
    },
    {
      original: product.gallery.image4,
      thumbnail: product.gallery.image4,
    },   
    {
      original: product.gallery.image5,
      thumbnail: product.gallery.image5,
    },
    {
      original: product.gallery.image6,
      thumbnail: product.gallery.image6,
    },
    {
      original: product.gallery.image7,
      thumbnail: product.gallery.image7,
    },
    {
      original: product.gallery.image8,
      thumbnail: product.gallery.image8,
    },
  ];

  return ( 
    <>
      <PageTitle title={product.name} page={"Product Single"} />
      <section className='product-single'>

        <div className='product-details'>

          <div className='product-des'>

            <h3>{product.name}</h3> {/* Product Name */}

            <div className='product-intro'>

              {/* Categories */}
              <div className='categories'> 
                {product.category && product.category.map((item, index) => {
                  return(          
                    <Link
                      key={index}
                      to={`/Shop/category/${item}`}>
                      {item}
                    </Link>
                  ) 
                })} 
              </div>

              <a
                href={product.demoLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn"
              >
                Live Demo
              </a>

            </div>

            {/* Product Image Gallery */}
            <div className='product-image'>
              <ImageGallery items={image} />
            </div>

            {/* Full Content / Description */}
            {product.content && (
              <div className='product-content'>
                <h4>About This Template</h4>
                <p>{product.description}</p>
              </div>
            )}

            {/* Features Section */}
            {product.features && product.features.length > 0 && (
              <div className='product-content'>
                <h4>⚡ Key Features</h4>
                <ul className='features-list'>
                  {product.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Feature-Rich Sections */}
            {product.sections && product.sections.length > 0 && (
              <div className='product-content'>
                <h4>⚡ Feature-Rich Sections</h4>
                <ul className='sections-list'>
                  {product.sections.map((section) => (
                    <li key={section.id}>
                      <strong>{section.title}:</strong> {section.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Template Forms Section */}
            {product.forms && product.forms.length > 0 && (
              <div className='product-content'>
                <h4>📧 Template Forms (Powered by PHPMailer)</h4>
                {product.forms.map((form) => (
                  <div key={form.id} className='form-item'>
                    <strong>{form.name}</strong>
                    <p>{form.description}</p>
                    <small>Technology: {form.technology}</small>
                  </div>
                ))}
                <p className='forms-note'>
                  All forms are powered by PHPMailer (Basic Authentication) using Username & App Password, ensuring safe, reliable, and spam-free submissions.
                </p>
              </div>
            )}

            {/* Fully Responsive Section */}
            {product.description && (
              <div className='product-content'>
                <h4>📱 Fully Responsive & Cross-Browser Optimized</h4>
                <p>{product.description}</p>
              </div>
            )}

            {/* Perfect For Section */}
            {product.perfectFor && product.perfectFor.length > 0 && (
              <div className='product-content'>
                <h4>🎯 Perfect For</h4>
                <ul className='perfect-for-list'>
                  {product.perfectFor.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* What You Get Section */}
            {product.whatYouGet && product.whatYouGet.length > 0 && (
              <div className='product-content'>
                <h4>📦 What You Get</h4>
                <ul className='what-you-get-list'>
                  {product.whatYouGet.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

          </div>

          <div className='sidebar'>

            <div className='sidebar-item'>
              <div className='price-intro'>
                <h5>Price:</h5>
                <h6>${product.price.toFixed(2)}</h6>
              </div>
              <a 
                href={product.buyLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn"
              >
                buy now
              </a>
            </div>

            {/* Tags */}
            <div className='tags sidebar-item'>
                  <div className='sidebar-heading'>
      <h2>Tags</h2> {/* Sidebar Heading Title */}
    </div>
              {/* Render tags */}
              <div className='box-container'>
                {(product.tags).map((item, index) => {
                  return(                        
                    <Link
                      className='tag-item'
                      key={index}
                      to={`/Shop/tag/${item}`}>
                      {item}
                    </Link>
                  )
                })}
              </div>

            </div>

          </div>

        </div>

        {/* Related Products */}
        <div className='related-items'>
          {/* Sidebar Heading */}
          <SidebarHeading title='Related Products' />

          {/* Related Product List */}
          <div className='box-container'>
            {
              filteredRelatedProducts.map((product) => {
                return(
                  <Product key={product.id} product={product} />
                )
              })
            }
          </div>
        </div>
      </section>
    </>
  )
}

export default ProductSingle;